import { Component, OnInit, Input, ViewChild, AfterViewChecked } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { UserStorageService } from 'src/app/services/user-storage-service/user-storage.service';
import { RulesTurnsService } from 'src/app/services/turns-services/rules-turns-service/rules-turns.service';
import { tap, flatMap } from 'rxjs/operators';
import { DesksTurnsService } from 'src/app/services/turns-services/desks-turns-service/desks-turns.service';
import { SnackBarService } from '../../shared-components/snack-bar/snack-bar.service';
import { CdkDragMove, CdkDropList, CdkDropListGroup, CdkDrag, moveItemInArray } from '@angular/cdk/drag-drop';
import { ViewportRuler } from '@angular/cdk/overlay';

@Component({
  selector: 'app-turns-rules-info-card',
  templateUrl: './turns-rules-info-card.component.html',
  styleUrls: ['./turns-rules-info-card.component.scss']
})
export class TurnsRulesInfoCardComponent implements OnInit, AfterViewChecked {

  @ViewChild(CdkDropListGroup, { static: false }) listGroup: CdkDropListGroup<CdkDropList>;
  @ViewChild(CdkDropList, { static: false }) placeholder: CdkDropList;
  target: CdkDropList;
  targetIndex: number;
  source: CdkDropList;
  sourceIndex: number;
  dragIndex: number;
  activeContainer;
  viewChecked = false;

  @Input() actions;
  rules;
  centerList;
  deskList;
  operators = [{
    InternalId: 'GREATER_OR_EQUAL',
    Value: '>='
  }, {
    InternalId: 'LOWER_OR_EQUAL',
    Value: '<='
  }, {
    InternalId: 'NO_APPLY',
    Value: 'whatever'
  }];
  basicDataForm = new FormGroup({
    selectedCenter: new FormControl(),
    ruleOrder: new FormControl(null, Validators.required),
    ruleOperator: new FormControl(null, Validators.required),
    ruleValue: new FormControl(null, Validators.required),
    ruleAction: new FormControl(null, Validators.required),
    selectedDesk: new FormControl(null, Validators.required)
  });

  constructor(private userStorageService: UserStorageService,
              private rulesTurnsService: RulesTurnsService,
              private viewportRuler: ViewportRuler,
              private snackBarService: SnackBarService,
              private desksTurnsService: DesksTurnsService) {
                this.target = null;
                this.source = null;
  }

  ngOnInit() {
    this.centerList = this.userStorageService.getCenterList();
    this.basicDataForm.patchValue({
      selectedCenter: this._getSelectedCenterInternalId()
    });
    this.rulesTurnsService.listenRefreshRules().pipe(
      tap(o => this._getRules())
    ).subscribe();
  }

  public addRule() {
    const validation = this.validateForm();
    if (validation === true) {
      this.basicDataForm.patchValue({
        ruleOrder: this.rules.length + 1
      });
      this.rulesTurnsService.addRule(this.basicDataForm).pipe(
        tap(o => this.rulesTurnsService.refreshRules()),
        tap(o => this._resetForm())
      ).subscribe();
    } else {
      this.snackBarService.open(validation, 'error');
    }
  }

  public updateRulesOrder(rules) {
    const rulesOrdered = [];
    rules.forEach((r, i) => rulesOrdered.push({internalId: r.InternalId, ruleOrder: i + 1}));
    this.rulesTurnsService.updateRulesOrder(rulesOrdered).pipe(
      tap(o => this.rulesTurnsService.refreshRules())
    ).subscribe();
  }

  public centerSelectedChanged(e) {
    this._getRules();
  }

  private validateForm() {
    if (!this.basicDataForm.get('selectedDesk').valid) {
      return 'turns-page.snackbar messages.selected desk error';
    } else if (!this.basicDataForm.get('ruleAction').valid) {
      return 'turns-page.snackbar messages.rule action error';
    } else if (!this.basicDataForm.get('ruleOperator').valid) {
      return 'turns-page.snackbar messages.rule operator error';
    } else if (this.basicDataForm.get('ruleOperator').value.InternalId !== 'NO_APPLY' && !this.basicDataForm.get('ruleValue').valid) {
      return 'turns-page.snackbar messages.rule value error';
    } else {
      return true;
    }
  }

  private _getRules() {
    this.rulesTurnsService.getRules(this.basicDataForm.get('selectedCenter').value.InternalId).pipe(
      tap(rules => this.rules = rules),
      flatMap(rules => this.desksTurnsService.getDesks()),
      tap(desks => this.deskList = this._filterDesks(desks))
    ).subscribe();
  }

  private _getSelectedCenterInternalId() {
    if (this.centerList.length === 1) {
      return this.centerList[0].InternalId;
    } else {
      if (this.basicDataForm.get('selectedCenter').value) {
        return this.basicDataForm.get('selectedCenter').value.InternalId;
      } else {
        return null;
      }
    }
  }

  private _filterDesks(desks) {
    return desks.filter(d => (d.CenterInternalId === this.basicDataForm.get('selectedCenter').value.InternalId) && (d.TurnTypeInternalId === 'MSHMV4BJ9THXZL9CIYPT25T6R7427OY42'));
  }

  public setPreselected(op1, op2) {
    if (op1 && op2) {
      return op1.InternalId === op2.InternalId;
    }
  }

  private _resetForm() {
    this.basicDataForm.patchValue({
      ruleOrder: null,
      ruleOperator: null,
      ruleValue: null,
      ruleAction: null,
      selectedDesk: null
    });
  }

  // ******************************************************************************************************************* //
  // **************************************************  DRAG & DROP  ************************************************** //
  // ******************************************************************************************************************* //

  ngAfterViewChecked() {
    if (this.rules && !this.viewChecked && this.placeholder) {
      const phElement = this.placeholder.element.nativeElement;
      phElement.style.display = 'none';
      phElement.parentElement.removeChild(phElement);
      this.viewChecked = true;
    }
  }

  public dragMoved(e: CdkDragMove) {
    const point = this.getPointerPositionOnPage(e.event);

    this.listGroup._items.forEach(dropList => {
      if (__isInsideDropListClientRect(dropList, point.x, point.y)) {
        this.activeContainer = dropList;
        return;
      }
    });
  }

  public dropListDropped() {
    if (!this.target) {
      return;
    }
    const phElement = this.placeholder.element.nativeElement;
    const parent = phElement.parentElement;
    phElement.style.display = 'none';
    parent.removeChild(phElement);
    parent.appendChild(phElement);
    parent.insertBefore(this.source.element.nativeElement, parent.children[this.sourceIndex]);
    this.target = null;
    this.source = null;
    if (this.sourceIndex !== this.targetIndex) {
      moveItemInArray(this.rules, this.sourceIndex, this.targetIndex);
      this.updateRulesOrder(this.rules);
    }
  }

  public dropListEnterPredicate = (drag: CdkDrag, drop: CdkDropList) => {
    if (drop === this.placeholder) {
      return true;
    }
    if (drop !== this.activeContainer) {
      return false;
    }
    const phElement = this.placeholder.element.nativeElement;
    const sourceElement = drag.dropContainer.element.nativeElement;
    const dropElement = drop.element.nativeElement;
    const dragIndex = __indexOf(dropElement.parentElement.children, (this.source ? phElement : sourceElement));
    const dropIndex = __indexOf(dropElement.parentElement.children, dropElement);
    if (!this.source) {
      this.sourceIndex = dragIndex;
      this.source = drag.dropContainer;
      phElement.style.width = sourceElement.clientWidth + 'px';
      phElement.style.height = sourceElement.clientHeight + 'px';
      sourceElement.parentElement.removeChild(sourceElement);
    }
    this.targetIndex = dropIndex;
    this.target = drop;
    phElement.style.display = '';
    dropElement.parentElement.insertBefore(phElement, (dropIndex > dragIndex ? dropElement.nextSibling : dropElement));
    this.placeholder.enter(drag, drag.element.nativeElement.offsetLeft, drag.element.nativeElement.offsetTop);
    return false;
  }

  public getPointerPositionOnPage(event: MouseEvent | TouchEvent) {
    const point = __isTouchEvent(event) ? (event.touches[0] || event.changedTouches[0]) : event;
    const scrollPosition = this.viewportRuler.getViewportScrollPosition();
    return {
      x: point.pageX - scrollPosition.left,
      y: point.pageY - scrollPosition.top
    };
  }
}

function __indexOf(collection, node) {
  return Array.prototype.indexOf.call(collection, node);
}

function __isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
  return event.type.startsWith('touch');
}

function __isInsideDropListClientRect(dropList: CdkDropList, x: number, y: number) {
  const { top, bottom, left, right } = dropList.element.nativeElement.getBoundingClientRect();
  return y >= top && y <= bottom && x >= left && x <= right;
}
